'use client';

import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { BottomMobileMenu } from '@/components/platform/navigation';
import type { BottomMenuItem, CenterAction } from '@/components/platform/navigation';
import { PlusIcon } from '@heroicons/react/24/outline';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import {
  navigationTree,
  filterNavigationTree,
} from '@/lib/platform/navigation/get-first-accessible-page';

/**
 * Section-aware Bottom Mobile Menu
 *
 * Automatically shows relevant navigation items based on the current section:
 * - VALUATIONS: Dashboard, Valuations, Reports, Methods
 * - SPONSORSHIPS: Sponsors, Packages, Inventory, Proposals, Analytics
 * - PLATFORM: Users, Organisation, Documents, Settings, Billing, Plugins, API
 *
 * Respects user permissions - only shows items the user has access to.
 */
export default function SectionBottomMenu({
  shiftLeftForOrb = false,
}: {
  /** Shift the menu left to make room for the AI orb on the right. */
  shiftLeftForOrb?: boolean;
} = {}) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: permissions, isLoading: permissionsLoading } = usePermissionsQuery();
  const { features } = useEntitlements();

  // Determine current section based on pathname
  const currentSection = useMemo(() => {
    // Valuations section - includes rewritten routes
    if (
      pathname?.startsWith('/products/valuations/') ||
      pathname?.startsWith('/valuation/') ||
      pathname === '/dashboard' ||
      pathname === '/valuations' ||
      pathname?.startsWith('/valuations/') ||
      pathname === '/reports' ||
      pathname?.startsWith('/reports/') ||
      pathname === '/methods'
    )
      return 'valuations';

    // Sponsorships section
    if (pathname?.startsWith('/products/sponsorships/')) return 'sponsorships';

    // Platform section - includes rewritten routes
    if (
      pathname?.startsWith('/platform/') ||
      pathname === '/users' ||
      pathname?.startsWith('/users/') ||
      pathname === '/settings' ||
      pathname === '/billing' ||
      pathname?.startsWith('/billing/') ||
      pathname === '/profile' ||
      pathname === '/plugins' ||
      pathname === '/api-management' ||
      pathname === '/welcome' ||
      pathname === '/audit' ||
      pathname === '/reactivate' ||
      pathname === '/admin' ||
      pathname?.startsWith('/admin/')
    )
      return 'platform';

    return null;
  }, [pathname]);

  // Filter navigation tree based on permissions and plan limits
  const filteredNavigation = useMemo(() => {
    if (!permissions) return navigationTree;
    return filterNavigationTree(navigationTree, permissions, features);
  }, [permissions, features]);

  // Build menu items from filtered navigation tree for current section
  const currentItems = useMemo<BottomMenuItem[]>(() => {
    if (!currentSection) return [];

    // Find the section in the filtered navigation tree
    const section = filteredNavigation.find((item) => item.id === currentSection);
    if (!section?.children) return [];

    // Convert navigation items to bottom menu items (limit to 5 items, filter out items without icons)
    return section.children
      .filter((child) => child.icon !== undefined)
      .slice(0, 5)
      .map((child) => ({
        icon: child.icon!,
        label: child.name,
        href: child.href || '#',
      }));
  }, [currentSection, filteredNavigation]);

  // Center action button - only for valuations section
  const centerAction: CenterAction | undefined = useMemo(() => {
    if (currentSection === 'valuations') {
      return {
        icon: PlusIcon,
        label: 'Create new valuation',
        onClick: () => router.push('/valuation/new'),
      };
    }
    return undefined;
  }, [currentSection, router]);

  // Don't render while permissions are loading OR if permissions are empty/default (prevents flash of empty menu)
  // Check for empty roles array which indicates default/placeholder permissions
  if (permissionsLoading || !permissions?.roles || permissions.roles.length === 0) {
    return null;
  }

  // Don't render if no section detected or no items
  if (!currentSection || currentItems.length === 0) {
    return null;
  }

  return (
    <BottomMobileMenu
      variant="rounded-cta"
      items={currentItems}
      centerAction={centerAction}
      showLabels={false}
      shiftLeftForOrb={shiftLeftForOrb}
      className="md:hidden" // Only show on mobile
    />
  );
}
